import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ChevronDoubleLeftIcon } from "@heroicons/react/solid";
import Hero from "components/lib/Global/Hero";
import DashboardWrapper from "components/lib/Global/DashboardWrapper";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import MainSidebar from "./MainSidebar";
import MinuteBookSidebar from "./MinuteBookSidebar";
import { MinuteBookContext } from "context/MinuteBookContext";
import { minuteBookSidebarItems } from "./minuteBookSidebarItems";
import { flattenNestedChildren } from "utils";
import { getAccessRequests } from "state/slices/accessRequest";
import {
  getCompanyBeneficialOwnersAsync,
  getCompanyDashboardAsync,
  getCompanyOfficialsAsync,
  getManageCompanyTransactions,
  resetCompanyDashboard,
} from "state/slices/companyDashboard";
import CompanyDashboardContext from "context/CompanyDashboardContext";
import { getAllInitiateTransactionChangeAsync } from "state/slices/initiateTransactionChange";
import { getCompany } from "state/slices/company";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

const defaultTab = { id: "businessDetails", name: "Business Details" };

const CompanyDashboardWrapper = ({ isForHistory, children }) => {
  const { companyId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { getUserStatus } = useSelector((state) => state.authSlice);
  const { company, getCompanyStatus, breadCrumbs } = useSelector(
    (state) => state.companyDashboardSlice
  );

  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const currentUser = useSelector((state) => state.userSlice);
  const [selectedManageModuleTab, setSelectedManageModuleTab] = useState("");
  const [selectedChangesTab, setSelectedChangesTab] = useState("");
  const [selectedOfficial, setSelectedOfficial] = useState("");
  const [showChangesAndApprovals, setShowChangesAndApprovals] = useState(false);
  const [newlyCreatedTransaction, setNewlyCreatedTransaction] = useState(null);

  useEffect(() => {
    if (companyId && currentUser?._id) {
      dispatch(getCompanyDashboardAsync({ companyId }));
      dispatch(
        getCompanyOfficialsAsync({
          companyId,
          queryParams: { isDashboard: true },
        })
      );
      dispatch(getCompanyBeneficialOwnersAsync({ companyId }));
      dispatch(getAccessRequests({ query: { createdBy: currentUser._id } }));
      dispatch(
        getManageCompanyTransactions({
          companyId,
        })
      );
      dispatch(
        getAllInitiateTransactionChangeAsync({
          companyId,
          params: {
            limit: 10,
            skip: (1 - 1) * 10,
            search: "",
            sort: "updatedAt",
            order: "desc",
          },
        })
      );
      dispatch(getCompany({ id: companyId }));
    }
    return () => {
      dispatch(resetCompanyDashboard());
    };
  }, [companyId, currentUser?._id, dispatch]);

  const companyName = company?.registrationName ?? company?.companyName1;

  let pages =
    breadCrumbs.length > 0
      ? [
        { name: "My Companies", href: "/companies", current: false },
        ...breadCrumbs,
      ]
      : [
        { name: "My Companies", href: "/companies", current: false },
        companyName
          ? {
            name: companyName,
            href: "#",
            current: true,
          }
          : null,
      ];

  const goToBusinessDetails = () =>
    setSelectedTab({ id: "businessDetails", name: "Business Details" });

  const goToTransactionList = () =>
    setSelectedTab({ id: "transactions", name: "Transactions" });

  const handleMakeApplication = () => {
    history.push(`/companies/${companyId}/dashboard?makeApplications`);
  };

  const resetSelectedChangesData = () => {
    setSelectedChangesTab("");
    setSelectedOfficial(null);
  };

  useEffect(() => {
    if (selectedTab.id !== "manageYourCompany") {
      setSelectedManageModuleTab(null);
    }
  }, [selectedTab]);

  //Select default on edit history transaction
  useEffect(() => {
    setSelectedTab({
      id: "history",
      label: "History",
    });
  }, [isForHistory]);

  return (
    <CompanyDashboardContext.Provider
      value={{
        resetSelectedChangesData,
        selectedManageModuleTab,
        selectedChangesTab,
        selectedOfficial,
        showChangesAndApprovals,
        setCompanyDashboardTab: setSelectedTab,
        setSelectedChangesTab,
        setSelectedManageModuleTab,
        setSelectedOfficial,
        setShowChangesAndApprovals,
        goToTransactionList,
        newlyCreatedTransaction,
        setNewlyCreatedTransaction,
      }}
    >
      <DashboardWrapper pages={pages} showBreadCrumbs>
        <MinuteBookContext.Provider
          value={{
            selectedTab,
            setSelectedTab,
            goToTransactionList,
            individualSidebarItems: flattenNestedChildren(
              minuteBookSidebarItems
            ),
          }}
        >
          {currentUser && currentUser?.isOnboarded && (
            <div className="grid gap-10 py-10 mx-auto">
              <Hero
                heading={
                  company?.registrationName ??
                  <SpinnerIcon className="text-gray-400" />
                }
                description=""
              />
              <div className="grid grid-cols-5 border-t border-gray">
                {selectedTab.id === "minuteBook" ||
                  selectedTab?.renderMinuteBook ? (
                  <div className="col-span-5 md:col-span-1 pt-8">
                    <div
                      className="flex items-center my-2 text-gray-600"
                      onClick={() => setSelectedTab(defaultTab)}
                    >
                      <ChevronDoubleLeftIcon className="w-5 h-5 mr-2" />
                      <span className="cursor-pointer">
                        Back to main sidebar
                      </span>
                    </div>
                    <MinuteBookSidebar
                      selectedTab={selectedTab}
                      setSelectedTab={setSelectedTab}
                      goToBusinessDetails={goToBusinessDetails}
                    />
                  </div>
                ) : (
                  <div>
                    <MainSidebar
                      selectedTab={selectedTab}
                      setSelectedTab={setSelectedTab}
                      isForHistory={isForHistory}
                    />
                    <div className="border-t pt-2">
                      <button
                        className="bg-sflPrimary w-full text-white font-bold py-2 px-4 rounded mb-4"
                        onClick={handleMakeApplication}
                      >
                        Make Application
                      </button>
                    </div>
                  </div>
                )}
                <div className="col-span-5 md:border-l md:col-span-4 md:pl-11 pt-8 border-gray">
                  {getUserStatus.status === "loading" ||
                    getCompanyStatus.status === "loading" ? (
                    <div className="flex justify-center mx-auto text-white">
                      <SpinnerIcon className="text-gray-400" />
                    </div>
                  ) : (
                    <>{children}</>
                  )}
                </div>
              </div>
            </div>
          )}
        </MinuteBookContext.Provider>
      </DashboardWrapper>
    </CompanyDashboardContext.Provider>
  );
};

export default CompanyDashboardWrapper;
